import React, { useEffect, useState } from "react";
import { api, setArchitectKey } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
  BtnPrimary,
} from "@/components/cathedral/Primitives";

export default function Settings() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({
    use_byo_keys: false,
    gemini_key: "",
    anthropic_key: "",
    openai_key: "",
  });
  const [archKey, setArchKey] = useState(
    typeof window !== "undefined" ? window.localStorage.getItem("nx_architect_key") || "" : ""
  );
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [pubkey, setPubkey] = useState(null);
  const [sigPassphrase, setSigPassphrase] = useState("");
  const [sigMsg, setSigMsg] = useState(null);

  const load = () => api.settings().then(setS);
  const loadKey = () => api.pubkey().then(setPubkey).catch(() => {});
  useEffect(() => {
    load();
    loadKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveArchKey = () => {
    setArchitectKey(archKey);
    if (typeof window !== "undefined") {
      if (archKey) window.localStorage.setItem("nx_architect_key", archKey);
      else window.localStorage.removeItem("nx_architect_key");
    }
    setSaved("Architect key cached locally.");
  };

  const generateKey = async () => {
    setSigMsg(null);
    try {
      const r = await api.keygen(sigPassphrase);
      setSigMsg(`Cathedral key loaded · fingerprint ${r.public_fingerprint}`);
      setSigPassphrase("");
      loadKey();
    } catch (e) {
      setSigMsg(e.response?.data?.detail || e.message);
    }
  };

  const unloadSigner = async () => {
    try {
      await api.unloadKey();
      setSigMsg("Cathedral signer unloaded.");
      loadKey();
    } catch (e) {
      setSigMsg(e.response?.data?.detail || e.message);
    }
  };

  const submit = async () => {
    setError(null);
    setSaved(null);
    try {
      const body = { use_byo_keys: form.use_byo_keys };
      if (form.gemini_key) body.gemini_key = form.gemini_key;
      if (form.anthropic_key) body.anthropic_key = form.anthropic_key;
      if (form.openai_key) body.openai_key = form.openai_key;
      await api.updateSettings(body);
      setSaved("Saved. New requests will respect this configuration.");
      setForm({ use_byo_keys: form.use_byo_keys, gemini_key: "", anthropic_key: "", openai_key: "" });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <section data-testid="settings-page" className="px-8 lg:px-12 py-12 max-w-screen-2xl">
      <SectionHeader
        overline="DIR-053 · Technology-Agnostic Design"
        title="Settings"
        description="By default, the cathedral runs on the Emergent Universal LLM Key. You may swap to your own Google AI Studio or Anthropic keys at any time."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
        <Card className="p-10" data-testid="signatures-card">
          <Overline>Cathedral Signatures · Ed25519 · DIR-055 / DIR-057</Overline>
          <p className="text-sm text-[var(--text-3)] mt-3 leading-relaxed">
            Every gate-passing ledger entry can be signed with a key deterministically
            derived from your passphrase (PBKDF2-HMAC-SHA512, 600,000 iters). The
            private seed never touches disk; future stewards can re-derive the same
            key from the same passphrase alone (DIR-066).
          </p>
          <div className="mt-6 space-y-2 font-mono text-[11px]">
            <Tag tone={pubkey?.loaded ? "pass" : "default"}>
              {pubkey?.loaded ? "SIGNER LOADED" : "SIGNER UNLOADED"}
            </Tag>
            {pubkey?.public_fingerprint && (
              <div className="text-[var(--text-3)]">
                fingerprint · <span className="text-[var(--text)]">{pubkey.public_fingerprint}</span>
              </div>
            )}
            {pubkey?.public_hex && (
              <div className="text-[var(--text-3)] break-all">
                pubkey · {pubkey.public_hex}
              </div>
            )}
          </div>

          <input
            data-testid="sig-passphrase"
            type="password"
            placeholder="Architect passphrase (min 12 chars)"
            value={sigPassphrase}
            onChange={(e) => setSigPassphrase(e.target.value)}
            className="mt-6 w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
          />
          <div className="mt-4 flex gap-3">
            <BtnPrimary onClick={generateKey} data-testid="sig-keygen-btn" disabled={!sigPassphrase}>
              Derive &amp; Load Key
            </BtnPrimary>
            <BtnPrimary
              onClick={unloadSigner}
              className="!bg-[var(--void-2)] !text-[var(--text)] !border !border-[var(--cyan)] hover:!bg-[rgba(0,240,255,0.06)]"
              data-testid="sig-unload-btn"
            >
              Unload
            </BtnPrimary>
          </div>
          {sigMsg && <p className="mt-3 font-mono text-[11px] text-[var(--text-2)]">{sigMsg}</p>}
        </Card>

        <Card className="p-10">
          <Overline>Architect Key</Overline>
          <p className="text-sm text-[var(--text-3)] mt-3 leading-relaxed">
            Required for write operations (rotate node keys, update LLM settings).
            Cached locally only — never sent to MongoDB.
          </p>
          <input
            data-testid="settings-architect-key"
            type="password"
            value={archKey}
            onChange={(e) => setArchKey(e.target.value)}
            placeholder="X-Architect-Key"
            className="mt-6 w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
          />
          <div className="mt-6">
            <BtnPrimary onClick={saveArchKey} data-testid="settings-architect-save">
              Cache Architect Key
            </BtnPrimary>
          </div>
        </Card>

        <Card className="p-10">
          <Overline>LLM Key Strategy</Overline>
          <div className="mt-4 space-y-2">
            <Tag tone={s?.emergent_key_active ? "pass" : "unknown"}>
              Emergent Universal Key · {s?.emergent_key_active ? "ACTIVE" : "MISSING"}
            </Tag>
            <Tag tone={s?.use_byo_keys ? "pass" : "default"}>
              BYO Mode · {s?.use_byo_keys ? "ON" : "OFF"}
            </Tag>
          </div>

          {s && (
            <ul className="mt-6 font-mono text-[11px] text-[var(--text-3)] space-y-1">
              <li>gemini: {s.gemini_key_masked || "—"}</li>
              <li>anthropic: {s.anthropic_key_masked || "—"}</li>
              <li>openai: {s.openai_key_masked || "—"}</li>
            </ul>
          )}

          <div className="mt-8 space-y-4">
            <label className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-widest">
              <input
                type="checkbox"
                data-testid="settings-byo-toggle"
                checked={form.use_byo_keys}
                onChange={(e) => setForm({ ...form, use_byo_keys: e.target.checked })}
                className="accent-[var(--cyan)]"
              />
              Use my own provider keys
            </label>

            <input
              data-testid="settings-gemini-key"
              placeholder="Google AI Studio (Gemini) key"
              type="password"
              value={form.gemini_key}
              onChange={(e) => setForm({ ...form, gemini_key: e.target.value })}
              className="w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
            />
            <input
              data-testid="settings-anthropic-key"
              placeholder="Anthropic key (Claude Sonnet 4.5 fallback)"
              type="password"
              value={form.anthropic_key}
              onChange={(e) => setForm({ ...form, anthropic_key: e.target.value })}
              className="w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
            />
            <input
              data-testid="settings-openai-key"
              placeholder="OpenAI key (optional)"
              type="password"
              value={form.openai_key}
              onChange={(e) => setForm({ ...form, openai_key: e.target.value })}
              className="w-full border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
            />

            <BtnPrimary onClick={submit} data-testid="settings-save-btn">
              Save Configuration
            </BtnPrimary>

            {saved && <p className="font-mono text-[11px] text-[var(--text)] mt-3">{saved}</p>}
            {error && <p className="font-mono text-[11px] text-[#E53E3E] mt-3">{error}</p>}
          </div>
        </Card>
      </div>
    </section>
  );
}
